import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { Permission } from 'src/app/common/enums';
import { FilterOperators } from 'src/app/common/enums/filter-operators';
import { PropertyFilterOperator } from 'src/app/common/interfaces/property-filter-operator';
import { buildFilterArray } from 'src/app/common/utils/build-filter-array';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { Filter } from 'src/app/models/common/filter';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { EmployerObjectDictionaryDto } from 'src/app/models/dtos/employer-object-dictionary-dto';
import { AbsenceStatus } from 'src/app/models/enums/absence-status';
import { AbsenceService } from '../../data/absence.service';
import { SubordinateOptionDto } from '../../models/dtos/subordinates-node-dto';
import { DisableErrorStateMatcher } from 'src/app/shared/error-state-matchers/disable-error-state-matcher';

@Component({
  selector: 'app-absence-list-filters',
  templateUrl: './absence-list-filters.component.html',
  styleUrls: ['./absence-list-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AbsenceListFiltersComponent implements OnInit, OnDestroy {
  private absenceService = inject(AbsenceService);
  private dictionaryService = inject(DictionaryService);

  public static readonly operatorsMap: Map<string, PropertyFilterOperator> = new Map<string, PropertyFilterOperator>([
    ['firstName', { property: 'FirstName', operator: FilterOperators.Contains }],
    ['lastName', { property: 'LastName', operator: FilterOperators.Contains }],
    ['companyId', { property: 'CompanyId', operator: FilterOperators.Equal }],
    ['employerId', { property: 'EmployerId', operator: FilterOperators.Equal }],
    ['employerObjectId', { property: 'EmployerObjectId', operator: FilterOperators.Equal }],
    ['pesel', { property: 'Pesel', operator: FilterOperators.StringEqual }],
    ['absenceTypeId', { property: 'AbsenceTypeId', operator: FilterOperators.Equal }],
    ['statusId', { property: 'StatusId', operator: FilterOperators.Equal }],
    ['absenceSyncStatusId', { property: 'ExternalSyncStatusId', operator: FilterOperators.Equal }],
    ['startDate', { property: 'StartDate', operator: FilterOperators.DateGreaterThanOrEqual }],
    ['endDate', { property: 'EndDate', operator: FilterOperators.DateLessThanOrEqual }],
    ['year', { property: 'Year', operator: FilterOperators.Equal }],
    ['workingTimeDimension', { property: 'WorkingTimeDimension', operator: FilterOperators.Equal }],
    ['activeAgreement', { property: 'IsActiveAgreement', operator: FilterOperators.Equal }],
    ['subordinates', { property: 'Subordinates', operator: FilterOperators.Equal }],
  ]);


  constructor() { }

  @Input() formGroup!: UntypedFormGroup;
  public readonly isSentToExteralSystemPermissions: Permission[] = [Permission.ShowIsSentToExteralSystemFilter];
  public readonly matcher = new DisableErrorStateMatcher();

  @Output() filterGridData = new EventEmitter<Filter[]>();

  companies$: Observable<DictionaryItem[]> = of([]);
  employerObjects$: Observable<EmployerObjectDictionaryDto[]> = of([]);
  employers$: Observable<DictionaryItem[]> = of([]);
  absenceTypes$: Observable<DictionaryItem[]> = of([]);
  absenceStatuses$: Observable<DictionaryItem[]> = of([]);
  absenceSyncStatuses$: Observable<DictionaryItem[]> = of([]);
  workingTimeDimensions$: Observable<string[]> = of([]);
  subordinateOptions: SubordinateOptionDto[] = [];
  currentYear = new Date().getFullYear();

  get company(): UntypedFormControl {
    return this.formGroup.get('company') as UntypedFormControl;
  }

  get employer(): UntypedFormControl {
    return this.formGroup.get('employer') as UntypedFormControl;
  }

  get employerObject(): UntypedFormControl {
    return this.formGroup.get('employerObject') as UntypedFormControl;
  }

  get employerObjectId(): UntypedFormControl {
    return this.formGroup.get('employerObjectId') as UntypedFormControl;
  }

  get companyId(): UntypedFormControl {
    return this.formGroup.get('companyId') as UntypedFormControl;
  }

  get employerId(): UntypedFormControl {
    return this.formGroup.get('employerId') as UntypedFormControl;
  }

  private readonly unsubscribe$ = new Subject<void>();
  private readonly autocompleteDebounceTimeMs = 500;
  private readonly formChangeDebounceTimeMs = 1000;

  ngOnInit(): void {
    const selectedYear = this.formGroup.get('year')?.value as number | null;
    if (selectedYear) {
      this.currentYear = selectedYear;
    }

    this.onCompanyChange();
    this.onEmployerChange();
    this.onEmployerObjectChange();
    this.absenceService.getSupervaiorsFilterOptions().subscribe((data) => {
      this.subordinateOptions = data;
      this.setInitialSelection();
    });

    this.formGroup.valueChanges
      .pipe(
        debounceTime(this.formChangeDebounceTimeMs),
        map(() => (this.formGroup.invalid ? null : buildFilterArray(this.formGroup, AbsenceListFiltersComponent.operatorsMap))),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        filter((filters): filters is Filter[] => filters !== null),
        takeUntil(this.unsubscribe$),
      )
      .subscribe((filters) => {
        this.filterGridData.emit(filters);
      });

    this.absenceTypes$ = this.dictionaryService.getAbsenceTypes();
    this.absenceStatuses$ = this.dictionaryService
      .getAbsenceStatuses()
      .pipe(map((absenceStatuses) => absenceStatuses.filter((absenceStatus) => absenceStatus.Id !== AbsenceStatus.Draft)));
    this.absenceSyncStatuses$ = this.dictionaryService.getAbsenceSyncStatuses();
    this.workingTimeDimensions$ = this.absenceService.getWorkingTimeDimensionsFilterOptions();

    this.filterGridData.emit(buildFilterArray(this.formGroup, AbsenceListFiltersComponent.operatorsMap));

  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  displayValue = (value: DictionaryItem) => value?.Name ?? '';

  onYearChange = (year: number): void => {
    this.currentYear = year;
    this.formGroup.patchValue({ year });
  };

  private onEmployerObjectChange() {
    this.employerObjects$ = this.employerObject.valueChanges.pipe(
      debounceTime(this.autocompleteDebounceTimeMs),
      tap((value: unknown) => {
        const employerObjectId = this.tryGetDictionaryId(value);
        if (employerObjectId !== null) {
          this.formGroup.patchValue({ employerObjectId });
        } else if (!value) {
          this.employerObject.reset(undefined, { emitEvent: false });
          this.employerObjectId.reset();
        }
      }),
      switchMap((value: unknown) => (typeof value === 'string' && value ? this.dictionaryService.getEmployerObjects(value) : of([]))),      
      takeUntil(this.unsubscribe$)
    );
  }

  private onCompanyChange() {
    this.companies$ = this.company.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.autocompleteDebounceTimeMs),
      tap((value: unknown) => {
        const companyId = this.tryGetDictionaryId(value);
        if (companyId !== null) {
          this.formGroup.patchValue({ companyId });
        } else if (!value) {
          this.company.reset(undefined, { emitEvent: false });
          this.companyId.reset();
        }
      }),
      switchMap((value: unknown) => (typeof value === 'string' && value ? this.dictionaryService.getCompanies(value) : of([]))),
    );
  }

  private onEmployerChange() {
    this.employers$ = this.employer.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.autocompleteDebounceTimeMs),
      tap((value: unknown) => {
        const employerId = this.tryGetDictionaryId(value);
        if (employerId !== null) {
          this.formGroup.patchValue({ employerId });
        } else if (!value) {
          this.employer.reset(undefined, { emitEvent: false });
          this.employerId.reset();
        }
      }),
      switchMap((value: unknown) => (typeof value === 'string' && value ? this.dictionaryService.getEmployers(value) : of([]))),
    );
  }

  public setInitialSelection(): void {
    const selectedIds = this.formGroup.get('subordinates')?.value as string[] | null;
    if (!Array.isArray(selectedIds)) {
      return;
    }

    const availableIds = new Set(this.subordinateOptions.map((option) => option.Id));
    const normalized = selectedIds.filter((id) => availableIds.has(id));
    this.formGroup.patchValue({ subordinates: normalized }, { emitEvent: false });
  }

  private tryGetDictionaryId(value: unknown): number | null {
    if (!value || typeof value !== 'object' || !('Id' in value)) {
      return null;
    }

    const id = (value as { Id?: unknown }).Id;
    return typeof id === 'number' ? id : null;
  }
}
